import { Answers } from "@cur8/questionnaire";
import { localeToISO639Code } from "lib/i18n/localeToISO639Code";
import {
  DietEnum,
  DietOptions,
  MoreDietOptions,
  OtherDietOption,
} from "lib/questionnaire/question-flows/onboarding/questions/diet";
import { Trans as TransQuestions } from "lib/questionnaire/question-flows/onboarding/questions/trans";
import { useLocale } from "render/context/InternationalizationContext";
import { ChoiceInput } from "render/ui/presentation/Questionnaire/components/ChoiceInput";
import { TextInput } from "render/ui/presentation/Questionnaire/components/TextInput";
import { QuestionView } from "../../components/QuestionView";
import { Step, StepProps } from "../../step";
import { Trans } from "../../trans";

function DietTakenStep({
  answers,
  onAnswer,
  onNext,
  onPrev,
  currentQuestion,
  totalQuestions,
  onResetAnswer,
}: StepProps) {
  const answer = answers.get(DietOptions);
  const label = TransQuestions.Diet.Question();
  const { englishLabel } = TransQuestions.Diet.Question;
  const locale = useLocale();
  const languageCode = localeToISO639Code(locale);

  return (
    <QuestionView
      currentQuestion={currentQuestion}
      totalQuestionCount={totalQuestions}
      question={DietOptions}
      onNext={() => {
        onResetAnswer(DietOptions);
        onNext();
      }}
      onPrev={() => {
        onPrev();
        onResetAnswer(DietOptions);
      }}
      explanation={{
        openText: <Trans.General.WhyAreWeAsking />,
        closeText: <Trans.General.OkIUnderstand />,
        explanationText: <Trans.Steps.DietExplanation />,
      }}
    >
      <ChoiceInput
        question={DietOptions}
        answer={answer}
        onAnswer={(answer) => {
          onAnswer(DietOptions, {
            answer,
            questionAnswer: {
              questionId: "diet",
              englishLabel,
              label,
              languageCode,
              answer: {
                $type: "stringenum",
                value: DietOptions.getValue(answer) || "",
              },
            },
          });
          onNext();
        }}
      />
    </QuestionView>
  );
}

function MoreDietTakenStep({
  answers,
  onAnswer,
  onNext,
  onPrev,
  currentQuestion,
  totalQuestions,
  onResetAnswer,
}: StepProps) {
  const answer = answers.get(MoreDietOptions);
  const label = TransQuestions.Diet.Question();
  const { englishLabel } = TransQuestions.Diet.Question;
  const locale = useLocale();
  const languageCode = localeToISO639Code(locale);

  return (
    <QuestionView
      currentQuestion={currentQuestion}
      totalQuestionCount={totalQuestions}
      question={MoreDietOptions}
      onNext={() => {
        onResetAnswer(DietOptions);
        onNext();
      }}
      onPrev={() => {
        onPrev();
        onResetAnswer(MoreDietOptions);
      }}
      explanation={{
        openText: <Trans.General.WhyAreWeAsking />,
        closeText: <Trans.General.OkIUnderstand />,
        explanationText: <Trans.Steps.DietExplanation />,
      }}
    >
      <ChoiceInput
        question={MoreDietOptions}
        answer={answer}
        onAnswer={(answer) => {
          onAnswer(MoreDietOptions, {
            answer,
            questionAnswer: {
              questionId: "diet",
              englishLabel,
              label,
              languageCode,
              answer: {
                $type: "stringenum",
                value: MoreDietOptions.getValue(answer) || "",
              },
            },
          });
          onNext();
        }}
      />
    </QuestionView>
  );
}

function OtherDietTakenStep({
  answers,
  onAnswer,
  onNext,
  onPrev,
  currentQuestion,
  totalQuestions,
  onResetAnswer,
}: StepProps) {
  const answer = answers.get(OtherDietOption);
  const label = TransQuestions.Diet.Question();
  const { englishLabel } = TransQuestions.Diet.Question;
  const locale = useLocale();
  const languageCode = localeToISO639Code(locale);

  return (
    <QuestionView
      currentQuestion={currentQuestion}
      totalQuestionCount={totalQuestions}
      question={OtherDietOption}
      onNext={onNext}
      onPrev={() => {
        onPrev();
        onResetAnswer(OtherDietOption);
      }}
      cta={<Trans.General.NextQuestion />}
      explanation={{
        openText: <Trans.General.WhyAreWeAsking />,
        closeText: <Trans.General.OkIUnderstand />,
        explanationText: <Trans.Steps.DietExplanation />,
      }}
    >
      <TextInput
        placeholder={Trans.General.WriteYourAnswer()}
        answer={answer}
        onAnswer={(answer) => {
          onAnswer(OtherDietOption, {
            answer,
            questionAnswer: {
              questionId: "diet",
              englishLabel,
              label,
              languageCode,
              answer: {
                $type: "text",
                value: OtherDietOption.getValue(answer).trim(),
              },
            },
          });
        }}
      />
    </QuestionView>
  );
}

export function filter(steps: Step[], answers: Answers) {
  steps.push(DietTakenStep);

  if (answers.lookup(DietOptions) === DietEnum.More) {
    steps.push(MoreDietTakenStep);
  }
  if (answers.lookup(MoreDietOptions) === DietEnum.Other) {
    steps.push(OtherDietTakenStep);
  }
}
